<script setup>
import { computed } from 'vue';
import { useId } from 'vue';
import { VolumeUp16 } from '@carbon/icons-vue';
import * as Sentry from '@sentry/vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';

const props = defineProps({
  value: {
    type: String,
    required: true,
    validator: value =>
      ['ding', 'bell', 'chime', 'magic', 'ping'].includes(value),
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);

const alertTones = computed(() => [
  {
    value: 'ding',
    label: 'Ding',
  },
  {
    value: 'bell',
    label: 'Bell',
  },
  {
    value: 'chime',
    label: 'Chime',
  },
  {
    value: 'magic',
    label: 'Magic',
  },
  {
    value: 'ping',
    label: 'Ping',
  },
]);

const selectedValue = computed({
  get: () => props.value,
  set: value => {
    emit('change', value);
  },
});

// Phase 9.5: the kit's OptionSelect and a quiet icon button to hear the tone.
const labelId = `alert-tone-${useId()}`;
const audio = new Audio();

const playAudio = async () => {
  try {
    // Has great support https://caniuse.com/mdn-api_htmlaudioelement
    audio.src = `/audio/dashboard/${selectedValue.value}.mp3`;
    await audio.play();
  } catch (error) {
    Sentry.captureException(error);
  }
};
</script>

<template>
  <div class="grid gap-2">
    <p :id="labelId" class="type-polaris-label">{{ label }}</p>
    <div class="flex items-center gap-2">
      <OptionSelect
        v-model="selectedValue"
        :options="alertTones"
        :aria-labelledby="labelId"
        class="w-full sm:w-48"
      />
      <Button
        v-tooltip.top="
          $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.PLAY')
        "
        variant="outline"
        size="icon"
        type="button"
        :aria-label="
          $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.PLAY')
        "
        @click="playAudio"
      >
        <VolumeUp16 aria-hidden="true" />
      </Button>
    </div>
  </div>
</template>
